import { useEffect, useRef, useState, useCallback } from 'react';
import Swal from 'sweetalert2';
import {
  INACTIVITY_LOGOUT_MS,
  SESSION_WARNING_BEFORE_MS,
  SESSION_WARNING_FIRE_MS,
  getInactivityLogoutUserMessage,
} from '../constants/session';
import { performAutoLogout } from '../utils/uiHandlers';
import { notifyDesktopViaServiceWorkerOrFallback } from '../utils/webPush';
import { subscribeUserActivity } from '../utils/sessionActivityHub';

/** Clases del popup de Swal propias de este módulo (para cerrarlas al detectar actividad). */
const SESSION_SWAL_POPUP_CLASSES = ['swal-session-warning', 'swal-session-critical'];

function getSessionSwalTarget() {
  return document.getElementById('swal-root') || document.body;
}

function setModalRootBlocked(blocked) {
  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return;
  if (blocked) {
    modalRoot.dataset.swalBlocked = 'true';
  } else {
    delete modalRoot.dataset.swalBlocked;
  }
}

function isSessionSwalPopup(popup) {
  if (!popup) return false;
  return SESSION_SWAL_POPUP_CLASSES.some((cls) => popup.classList.contains(cls));
}

function findSessionSwalPopupInDom() {
  return document.querySelector(
    `.swal2-popup.${SESSION_SWAL_POPUP_CLASSES.join(', .swal2-popup.')}`,
  );
}

/**
 * Cierra y limpia modales de inactividad (warning / último aviso).
 */
function closeSessionSwalIfOpen() {
  const popup = typeof Swal.getPopup === 'function' ? Swal.getPopup() : null;
  const ours = isSessionSwalPopup(popup) || findSessionSwalPopupInDom();
  if (!ours && !(typeof Swal.isVisible === 'function' && Swal.isVisible())) {
    return;
  }
  if (!ours) return;

  if (typeof Swal.isVisible === 'function' && Swal.isVisible()) {
    Swal.close();
  }

  document.querySelectorAll('.swal2-container').forEach((container) => {
    const sessionPopup = container.querySelector(
      `.swal2-popup.${SESSION_SWAL_POPUP_CLASSES.join(', .swal2-popup.')}`,
    );
    if (sessionPopup) container.remove();
  });

  document.body.classList.remove('swal2-shown', 'swal2-height-auto', 'swal2-no-backdrop');
  const paddingRight = document.body.style.paddingRight;
  if (paddingRight) document.body.style.paddingRight = '';
  setModalRootBlocked(false);
}

/** No reiniciar el timer en capture si el usuario interactúa con el propio aviso de sesión. */
function shouldIgnoreActivityEvent(event) {
  const target = event?.target;
  if (!target || typeof target.closest !== 'function') return false;
  const popup = target.closest('.swal2-popup');
  return isSessionSwalPopup(popup);
}

function fireSessionSwal(options) {
  return Swal.fire({
    target: getSessionSwalTarget(),
    heightAuto: false,
    scrollbarPadding: false,
    returnFocus: false,
    customClass: {
      container: 'swal-session-container',
      ...(options.customClass || {}),
    },
    didOpen: () => {
      setModalRootBlocked(true);
      options.didOpen?.();
    },
    didDestroy: () => {
      setModalRootBlocked(false);
      options.didDestroy?.();
    },
    ...options,
  });
}

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
  'touchmove',
  'touchend',
];

/**
 * Inactividad: avisos, cierre de sesión tras INACTIVITY_LOGOUT_MS sin actividad,
 * notificaciones push y hub para que las vistas reinicien el temporizador.
 */
export default function SessionInactivityBanner() {
  const [visible, setVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.floor(SESSION_WARNING_BEFORE_MS / 1000),
  );
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const totalSecondsAtWarningRef = useRef(0);
  const oneMinuteNotifyDoneRef = useRef(false);
  const criticalSwalOpenRef = useRef(false);

  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const hideWarning = useCallback(() => {
    setVisible(false);
    clearCountdown();
  }, [clearCountdown]);

  const showWarningAndStartCountdown = useCallback(() => {
    const totalSeconds = Math.floor(SESSION_WARNING_BEFORE_MS / 1000);
    totalSecondsAtWarningRef.current = totalSeconds;
    oneMinuteNotifyDoneRef.current = false;
    criticalSwalOpenRef.current = false;
    setSecondsLeft(totalSeconds);
    setVisible(true);
    notifyDesktopViaServiceWorkerOrFallback(
      'Sesión por expirar',
      'Sin actividad en la web la sesión se cerrará. Interactúa con la aplicación para seguir conectado.',
      { tag: 'hs-session-warning', renotify: true, requireInteraction: false, url: '/', inPageToastWhenVisible: false },
    ).catch((err) => {
      console.warn('[SessionInactivityBanner] Error en notificación de advertencia:', err);
    });

    fireSessionSwal({
      icon: 'warning',
      title: '⚠️ Sesión por expirar',
      text: `Sin actividad la sesión se cerrará en ${Math.floor(SESSION_WARNING_BEFORE_MS / 60000)} minutos. Interactúa con la aplicación para seguir conectado.`,
      toast: false,
      position: 'center',
      showConfirmButton: true,
      confirmButtonText: 'Entendido',
      timer: 10000,
      timerProgressBar: true,
      allowOutsideClick: true,
      allowEscapeKey: true,
      width: 560,
      customClass: {
        popup: 'swal-session-warning',
        confirmButton: 'btn btn-warning',
      },
    });
    clearCountdown();
    countdownIntervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, [clearCountdown]);

  const scheduleWarning = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    closeSessionSwalIfOpen();
    criticalSwalOpenRef.current = false;
    hideWarning();
    warningTimerRef.current = setTimeout(() => {
      showWarningAndStartCountdown();
    }, SESSION_WARNING_FIRE_MS);
  }, [hideWarning, showWarningAndStartCountdown]);

  const scheduleLogout = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    logoutTimerRef.current = setTimeout(() => {
      logoutTimerRef.current = null;
      performAutoLogout(getInactivityLogoutUserMessage());
    }, INACTIVITY_LOGOUT_MS);
  }, []);

  const bumpSession = useCallback(() => {
    scheduleWarning();
    scheduleLogout();
  }, [scheduleWarning, scheduleLogout]);

  const keepSessionActive = useCallback(() => {
    closeSessionSwalIfOpen();
    criticalSwalOpenRef.current = false;
    hideWarning();
    bumpSession();
  }, [bumpSession, hideWarning]);

  useEffect(() => {
    return subscribeUserActivity(bumpSession);
  }, [bumpSession]);

  useEffect(() => {
    bumpSession();
    const handleActivity = (event) => {
      if (shouldIgnoreActivityEvent(event)) return;
      bumpSession();
    };
    ACTIVITY_EVENTS.forEach((ev) => {
      window.addEventListener(ev, handleActivity, { passive: true, capture: true });
    });
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => {
        window.removeEventListener(ev, handleActivity, { capture: true });
      });
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      clearCountdown();
      closeSessionSwalIfOpen();
    };
  }, [bumpSession, clearCountdown]);

  useEffect(() => {
    if (!visible) return;

    const total = totalSecondsAtWarningRef.current;
    const isOneMinuteMark =
      (total > 60 && secondsLeft === 60) || (total <= 60 && secondsLeft === total);

    if (isOneMinuteMark && !oneMinuteNotifyDoneRef.current && !criticalSwalOpenRef.current) {
      oneMinuteNotifyDoneRef.current = true;
      criticalSwalOpenRef.current = true;
      notifyDesktopViaServiceWorkerOrFallback(
        'Sesión por expirar',
        'La sesión se cerrará por inactividad en 1 minuto.',
        { tag: 'hs-session-inactivity-1m', renotify: true, requireInteraction: false, url: '/', inPageToastWhenVisible: false },
      ).catch((err) => {
        console.warn('[SessionInactivityBanner] Error en notificación de 1 minuto:', err);
      });

      fireSessionSwal({
        icon: 'error',
        title: '🚨 ¡Último aviso!',
        text: 'La sesión se cerrará por inactividad en 1 minuto.',
        toast: false,
        position: 'center',
        showConfirmButton: true,
        confirmButtonText: 'Mantener Activo',
        timer: 15000,
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: true,
        width: 580,
        customClass: {
          popup: 'swal-session-critical',
          confirmButton: 'btn btn-danger',
        },
      }).then((result) => {
        criticalSwalOpenRef.current = false;
        if (result.isConfirmed) {
          keepSessionActive();
        } else {
          closeSessionSwalIfOpen();
        }
      });
    }
  }, [visible, secondsLeft, keepSessionActive]);

  return null;
}
